/*
 * Destaques do produto (sections/bracci-destaques.liquid): paginação.
 *
 * A faixa é uma lista com rolagem horizontal. Cada página mostra N ícones
 * (--bdst-columns no CSS: 3 no desktop, 2 no tablet, 1 no celular).
 * A última página sempre fica cheia: a rolagem para no fim da lista, então com
 * 4 ícones e 3 por página a seta avança 1 e a página 2 mostra os 3 últimos.
 */
(() => {
  if (customElements.get('bracci-destaques')) return;

  class BracciDestaques extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-bdst-track]');
      this.prev = this.querySelector('[data-bdst-prev]');
      this.next = this.querySelector('[data-bdst-next]');
      this.dots = this.querySelector('[data-bdst-dots]');
      if (!this.track) return;

      this.addEventListener('click', this.onClick);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.resizeObserver = new ResizeObserver(() => this.sync(true));
      this.resizeObserver.observe(this.track);
      this.addEventListener('shopify:block:select', this.onBlockSelect);
      this.sync(true);
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
    }

    get items() {
      return [...this.track.querySelectorAll('[data-bdst-item]')];
    }

    /* Medidas da paginação */
    layout() {
      const items = this.items;
      const perPage = Math.max(1, parseInt(getComputedStyle(this.track).getPropertyValue('--bdst-columns'), 10) || 1);
      const step = items.length > 1 ? items[1].offsetLeft - items[0].offsetLeft : this.track.clientWidth;
      const pages = Math.max(1, Math.ceil(items.length / perPage));
      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      return { items, perPage, step, pages, maxScroll };
    }

    currentPage({ perPage, step, pages, maxScroll }) {
      if (maxScroll <= 1) return 0;
      if (this.track.scrollLeft >= maxScroll - 2) return pages - 1;
      return Math.min(pages - 1, Math.round(this.track.scrollLeft / (step * perPage)));
    }

    goTo(page) {
      const info = this.layout();
      const target = Math.max(0, Math.min(info.pages - 1, page));
      // Nunca passa do fim: a última página mostra os últimos N ícones
      const left = Math.min(target * info.perPage * info.step, info.maxScroll);
      this.track.scrollTo({ left, behavior: 'smooth' });
    }

    sync(rebuild = false) {
      const info = this.layout();
      const page = this.currentPage(info);
      const paged = info.pages > 1 && info.maxScroll > 1;

      this.classList.toggle('bdst--fits', !paged);

      [this.prev, this.next].forEach((button) => {
        if (button) button.hidden = !paged;
      });
      if (this.prev) this.prev.disabled = page === 0;
      if (this.next) this.next.disabled = page === info.pages - 1;

      if (!this.dots) return;
      const count = paged ? info.pages : 0;
      if (rebuild || this.dots.childElementCount !== count) {
        this.dots.replaceChildren();
        for (let index = 0; index < count; index += 1) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'bdst__dot';
          dot.dataset.bdstDot = String(index);
          dot.setAttribute('aria-label', `Página ${index + 1} de ${count}`);
          dot.appendChild(document.createElement('span'));
          this.dots.appendChild(dot);
        }
      }
      [...this.dots.children].forEach((dot, index) => {
        dot.classList.toggle('is-active', index === page);
        dot.setAttribute('aria-current', index === page ? 'true' : 'false');
      });
    }

    onScroll = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.sync();
      });
    };

    onClick = (event) => {
      if (!(event.target instanceof Element)) return;
      const info = this.layout();
      const page = this.currentPage(info);
      if (event.target.closest('[data-bdst-prev]')) this.goTo(page - 1);
      else if (event.target.closest('[data-bdst-next]')) this.goTo(page + 1);
      else {
        const dot = event.target.closest('[data-bdst-dot]');
        if (dot) this.goTo(Number(dot.dataset.bdstDot));
      }
    };

    onBlockSelect = (event) => {
      const item = event.target instanceof Element ? event.target.closest('[data-bdst-item]') : null;
      if (!item) return;
      const info = this.layout();
      this.goTo(Math.floor(info.items.indexOf(item) / info.perPage));
    };
  }

  customElements.define('bracci-destaques', BracciDestaques);
})();
